import React, {
  Dispatch,
  memo,
  SetStateAction,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { useTheme } from "@emotion/react";
import { AddPhoto, Danger, Info } from "@bale/ui";
import { strings } from "intl/fa";
import classNames from "classnames";
import imageInputstyles from "./ImageInput.module.scss";
import styles from "./ImageCropInput.module.scss";
import InputNote from "../inputNote/InputNote";
import ReactCrop, { centerCrop, Crop, makeAspectCrop } from "react-image-crop";
import FullpageModal from "../modal/fullpageModal/FullpageModal";
import "react-image-crop/dist/ReactCrop.css";
import { Outputs } from "hooks/useFileHandler";
import * as Sentry from "@sentry/react";
import ImageURLPreview from "./ImageURLPreview";

type Props = {
  croppedImageURL: string | undefined;
  setCroppedImageURL: Dispatch<SetStateAction<string | undefined>>;
  title?: string;
  error?: string;
  helpText?: string;
  beforeSelectLabel?: string;
  afterSelectLabel?: string;
} & Outputs;

const ASPECT_RATIO = 16 / 9;

function centerAspectCrop(
  mediaWidth: number,
  mediaHeight: number,
  aspect: number
) {
  return centerCrop(
    makeAspectCrop(
      {
        unit: "%",
        width: 90,
      },
      aspect,
      mediaWidth,
      mediaHeight
    ),
    mediaWidth,
    mediaHeight
  );
}

const ImageCropInput: React.FC<React.PropsWithChildren<Props>> = (props) => {
  const theme = useTheme();
  const [cropStatus, setCropStatus] = useState<Crop>();
  const initialCropStatusRef = useRef<Crop>();
  const imageInputRef = useRef<HTMLInputElement>(null);
  const imagePreviewRef = useRef<HTMLImageElement>(null);

  const handleClick = useCallback(() => {
    props.setValue(undefined);
    props.setCroppedImageURL(undefined);
    if (imageInputRef.current) imageInputRef.current.value = "";
    imageInputRef.current?.click();
  }, []);

  const handleCropConfirmation = () => {
    if (!props.file || !imagePreviewRef.current || !cropStatus) return;
    const canvas = document.createElement("canvas");

    const ctx = canvas.getContext("2d");
    if (!ctx) {
      Sentry.captureMessage("No 2d context");
      return;
    }
    const img = imagePreviewRef.current;
    const { naturalHeight, naturalWidth } = img;
    canvas.width = (cropStatus.width * naturalWidth) / 100;
    canvas.height = (cropStatus.height * naturalHeight) / 100;

    ctx.drawImage(
      img,
      (cropStatus.x * naturalWidth) / 100,
      (cropStatus.y * naturalHeight) / 100,
      (cropStatus.width * naturalWidth) / 100,
      (cropStatus.height * naturalHeight) / 100,
      0,
      0,
      (cropStatus.width * naturalWidth) / 100,
      (cropStatus.height * naturalHeight) / 100
    );
    const fileName = props.file?.name ?? "image.jpeg";
    canvas.toBlob((blob) => {
      if (blob) {
        props.setCroppedImageURL(URL.createObjectURL(blob));
      }
    }, fileName);
  };

  const handleCropCanceling = useCallback(() => {
    props.setValue(undefined);
  }, []);

  const handleCropReset = useCallback(() => {
    setCropStatus(initialCropStatusRef.current);
  }, []);

  const onLoaded = useCallback((e: React.SyntheticEvent<HTMLImageElement>) => {
    const { width, height } = e.currentTarget;
    const centerCropStatus = centerAspectCrop(width, height, ASPECT_RATIO);
    initialCropStatusRef.current = centerCropStatus;
    setCropStatus(centerCropStatus);
  }, []);

  const imgSrc = useMemo<string | undefined>(() => {
    if (props.file) {
      return URL.createObjectURL(props.file);
    }
  }, [props.file]);

  return (
    <>
      <div className={imageInputstyles.Wrapper}>
        {props.title && <p className={imageInputstyles.Title}>{props.title}</p>}

        {props.croppedImageURL ? (
          <ImageURLPreview
            imageFileURL={props.croppedImageURL}
            onEdit={handleClick}
            label={props.afterSelectLabel || strings.common.input.image.label}
          />
        ) : (
          <div
            onClick={handleClick}
            className={classNames(imageInputstyles.Container, {
              [imageInputstyles.HasError]: props.error,
            })}
          >
            <AddPhoto size={24} color={theme.colors.primary[50]} />
            <span className={imageInputstyles.Text}>
              {props.beforeSelectLabel || strings.common.input.image.label}
            </span>
          </div>
        )}

        {props.error ? (
          <InputNote
            icon={<Danger size={20} color={theme.colors.error} />}
            textClassName={imageInputstyles.AlertText}
            text={props.error}
          />
        ) : (
          props.helpText && (
            <InputNote
              icon={<Info size={20} color={theme.colors.dark[300]} />}
              text={props.helpText ?? ""}
            />
          )
        )}
      </div>
      <input
        ref={imageInputRef}
        className={imageInputstyles.Input}
        data-testid="image-input"
        type="file"
        hidden
        accept="image/jpeg, image/png"
        onChange={props.onChange}
      />

      {!!imgSrc && !props.croppedImageURL && (
        <FullpageModal className={styles.modal}>
          <ReactCrop
            crop={cropStatus}
            onChange={(crop, percentCrop) => setCropStatus(percentCrop)}
            aspect={ASPECT_RATIO}
            minWidth={160}
            minHeight={90}
            keepSelection
            className={styles.reactCropper}
          >
            <img
              ref={imagePreviewRef}
              alt="crop-area"
              src={imgSrc}
              className={styles.cropImage}
              onLoad={onLoaded}
            />
          </ReactCrop>
          <div className={styles.cropActions}>
            <div onClick={handleCropConfirmation}>
              {strings.common.button.submit}
            </div>
            <div onClick={handleCropReset}>{strings.common.button.restart}</div>
            <div onClick={handleCropCanceling}>
              {strings.common.button.cancel}
            </div>
          </div>
        </FullpageModal>
      )}
    </>
  );
};

export default memo(ImageCropInput);
